import type { RootState } from "../Store";
import { LoadingState } from "../LoadingState";
import { UserInitializationState } from "../../user/Models";
import { createSelector } from "@reduxjs/toolkit";

interface ApplicationInitialization {
  status: "loading" | "ready" | "error";
  error?: string;
}

export const selectApplicationInitialization = createSelector(
  [
    (state: RootState) => state.user,
    (state: RootState) => state.featureFlags.loadingState,
    (state: RootState) => state.purchasedProducts,
    (state: RootState) => state.multiSitesMetadata.loading,
    (state: RootState) => state.faults.multiSitesFaults.metadata.loading,
  ],
  (user, featureFlagsStatus, products, sitesStatus, faultsStatus): ApplicationInitialization => {
  if (user.initializationState !== UserInitializationState.Initialized) {
    return { status: "loading" };
  }
  if (user.siteIds.length === 0) {
    return { status: "ready" };
  }
  const dependencies = [
    { status: featureFlagsStatus, error: "Failed to load feature flags" },
    {
      status: products.loadingState,
      error: products.error ?? "Failed to load purchased products",
    },
    { status: sitesStatus, error: "Failed to load sites metadata" },
    { status: faultsStatus, error: "Failed to load faults metadata" },
  ];
  const failed = dependencies.find(({ status }) => status === LoadingState.Error);
  if (failed) {
    return { status: "error", error: failed.error };
  }
  return {
    status: dependencies.every(({ status }) => status === LoadingState.Complete) ? "ready" : "loading",
  };
  },
);
